import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export interface PromptOptions {
    title: string;
    description?: React.ReactNode;
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    confirmLabel?: string;
    /** default true — empty input keeps the action disabled */
    required?: boolean;
    /** default true — use Textarea */
    multiline?: boolean;
    maxLength?: number;
}

type PromptFn = (options: PromptOptions) => Promise<string | null>;

interface PromptState {
    options: PromptOptions;
    resolve: (value: string | null) => void;
}

const PromptContext = React.createContext<PromptFn | null>(null);

let hasWarnedNoProvider = false;

function fallbackPrompt(options: PromptOptions): Promise<string | null> {
    if (!hasWarnedNoProvider) {
        hasWarnedNoProvider = true;
        // eslint-disable-next-line no-console
        console.warn(
            '[usePromptDialog] No <PromptProvider> found in the tree. Falling back to window.prompt.'
        );
    }
    const message = options.description
        ? `${options.title}\n\n${typeof options.description === 'string' ? options.description : ''}`
        : options.title;
    return Promise.resolve(window.prompt(message, options.defaultValue ?? ''));
}

export const PromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = React.useState<PromptState | null>(null);
    const [value, setValue] = React.useState('');
    const resolvedRef = React.useRef(false);

    const promptDialog = React.useCallback<PromptFn>(options => {
        return new Promise<string | null>(resolve => {
            resolvedRef.current = false;
            setValue(options.defaultValue ?? '');
            setState({ options, resolve });
        });
    }, []);

    const settle = React.useCallback((result: string | null) => {
        if (resolvedRef.current) return;
        resolvedRef.current = true;
        setState(current => {
            current?.resolve(result);
            return null;
        });
        setValue('');
    }, []);

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            if (!open) {
                settle(null);
            }
        },
        [settle]
    );

    const options = state?.options;
    const required = options?.required ?? true;
    const multiline = options?.multiline ?? true;
    const isDisabled = required && value.trim().length === 0;

    return (
        <PromptContext.Provider value={promptDialog}>
            {children}
            <Dialog open={state !== null} onOpenChange={handleOpenChange}>
                {options && (
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{options.title}</DialogTitle>
                            {options.description && (
                                <DialogDescription>{options.description}</DialogDescription>
                            )}
                        </DialogHeader>
                        <div className="space-y-2 py-2">
                            {options.label && (
                                <Label htmlFor="prompt-dialog-input">{options.label}</Label>
                            )}
                            {multiline ? (
                                <Textarea
                                    id="prompt-dialog-input"
                                    autoFocus
                                    value={value}
                                    placeholder={options.placeholder}
                                    maxLength={options.maxLength}
                                    onChange={event => setValue(event.target.value)}
                                />
                            ) : (
                                <Input
                                    id="prompt-dialog-input"
                                    autoFocus
                                    value={value}
                                    placeholder={options.placeholder}
                                    maxLength={options.maxLength}
                                    onChange={event => setValue(event.target.value)}
                                />
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => settle(null)}>
                                Cancel
                            </Button>
                            <Button disabled={isDisabled} onClick={() => settle(value)}>
                                {options.confirmLabel ?? 'Confirm'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
        </PromptContext.Provider>
    );
};

export function usePromptDialog(): PromptFn {
    const context = React.useContext(PromptContext);
    if (!context) {
        return fallbackPrompt;
    }
    return context;
}
